import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatErr, compressImage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Camera, Save, Send, ArrowLeft, Info, AlertTriangle, ChevronDown, UserCheck, ShieldAlert, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";

function ChoiceButtons({ value, choices, onPick, colorMap }) {
  return (
    <div className="flex flex-wrap gap-2">
      {choices.map((c) => (
        <button
          key={c}
          onClick={() => onPick(c)}
          className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
            value === c ? colorMap?.[c] || "bg-[#14532D] text-white border-[#14532D]" : "bg-white text-stone-600 border-stone-300 hover:border-[#14532D]"
          }`}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

function QuestionInput({ q, resp, setResp }) {
  const set = (patch) => setResp({ ...resp, ...patch, na: false });
  const v = resp.value;
  switch (q.type) {
    case "yes_no":
      return <ChoiceButtons value={v} choices={["yes", "no"]} onPick={(c) => set({ value: c })} colorMap={{ yes: "bg-emerald-600 text-white border-emerald-600", no: "bg-rose-600 text-white border-rose-600" }} />;
    case "pass_fail":
      return <ChoiceButtons value={v} choices={["pass", "fail"]} onPick={(c) => set({ value: c })} colorMap={{ pass: "bg-emerald-600 text-white border-emerald-600", fail: "bg-rose-600 text-white border-rose-600" }} />;
    case "rating_5":
    case "rating_10": {
      const max = q.type === "rating_5" ? 5 : 10;
      return (
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
            <button key={n} onClick={() => set({ value: n })} className={`w-9 h-9 rounded-lg text-sm font-mono font-semibold border transition-colors ${v === n ? "bg-[#14532D] text-white border-[#14532D]" : "bg-white text-stone-600 border-stone-300 hover:border-[#14532D]"}`}>{n}</button>
          ))}
        </div>
      );
    }
    case "percentage":
    case "numeric":
      return <Input type="number" className="w-40 bg-white" placeholder={q.type === "percentage" ? "0-100 %" : "Value"} value={v ?? ""} onChange={(e) => set({ value: e.target.value === "" ? null : parseFloat(e.target.value) })} />;
    case "multiple_choice":
    case "dropdown":
      return (
        <Select value={v || ""} onValueChange={(val) => set({ value: val })}>
          <SelectTrigger className="w-64 bg-white"><SelectValue placeholder="Select..." /></SelectTrigger>
          <SelectContent>{(q.options.length ? q.options : ["Option 1", "Option 2"]).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
    case "date":
      return <Input type="date" className="w-48 bg-white" value={v || ""} onChange={(e) => set({ value: e.target.value })} />;
    case "time":
      return <Input type="time" className="w-40 bg-white" value={v || ""} onChange={(e) => set({ value: e.target.value })} />;
    case "image":
    case "signature":
      return <p className="text-xs text-stone-400">Use the photo button below to attach evidence.</p>;
    default:
      return <Textarea rows={2} className="bg-white" placeholder="Enter observation..." value={v || ""} onChange={(e) => set({ value: e.target.value })} />;
  }
}

export default function AuditExecution() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [template, setTemplate] = useState(null);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [attaching, setAttaching] = useState({});
  const [autoSave, setAutoSave] = useState("");
  const dirty = useRef(false);
  const fileRefs = useRef({});

  useEffect(() => {
    api.get(`/audits/${id}`).then(async (r) => {
      setAudit(r.data);
      setResponses(r.data.responses || {});
      const t = await api.get(`/templates/${r.data.template_id}`);
      setTemplate(t.data);
    }).catch((e) => toast.error(formatErr(e)));
  }, [id]);

  useEffect(() => {
    if (!dirty.current || !audit || audit.status === "completed" || audit.authorized === false) return;
    setAutoSave("saving");
    const t = setTimeout(async () => {
      try {
        await api.put(`/audits/${id}/responses`, { responses });
        dirty.current = false;
        setAutoSave("saved");
      } catch {
        setAutoSave("");
      }
    }, 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses]);

  if (!audit || !template)
    return <div className="flex justify-center py-24"><div className="w-8 h-8 border-2 border-[#14532D] border-t-transparent rounded-full animate-spin" /></div>;

  const allQuestions = template.sections.flatMap((s) => s.questions);
  const answered = allQuestions.filter((q) => {
    const r = responses[q.id];
    return r && (r.na || (r.value !== null && r.value !== undefined && r.value !== ""));
  }).length;
  const progress = Math.round((answered / Math.max(1, allQuestions.length)) * 100);

  const getResp = (qid) => responses[qid] || { value: null, na: false, comment: "", photos: [] };
  const setResp = (qid, r) => {
    dirty.current = true;
    setResponses((prev) => ({ ...prev, [qid]: r }));
  };

  const addPhoto = async (qid, file) => {
    if (!file) return;
    setAttaching((a) => ({ ...a, [qid]: true }));
    try {
      const dataUrl = await compressImage(file);
      dirty.current = true;
      setResponses((prev) => {
        const r = prev[qid] || { value: null, na: false, comment: "", photos: [] };
        return { ...prev, [qid]: { ...r, photos: [...(r.photos || []), { data: dataUrl, at: new Date().toISOString(), by: audit.auditor_name }] } };
      });
      toast.success("Photo attached — uploading");
    } catch {
      toast.error("Could not process that photo. Please retry, or pick it from the Gallery.");
    } finally {
      setAttaching((a) => ({ ...a, [qid]: false }));
    }
  };

  const saveProgress = async () => {
    setSaving(true);
    try {
      await api.put(`/audits/${id}/responses`, { responses });
      toast.success("Progress saved");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    const missing = allQuestions.filter((q) => {
      if (!q.mandatory) return false;
      const r = responses[q.id];
      return !r || (!r.na && (r.value === null || r.value === undefined || r.value === ""));
    });
    if (missing.length > 0) return toast.error(`${missing.length} mandatory question(s) unanswered. Mark N/A if not applicable.`);
    setSubmitting(true);
    try {
      const { data } = await api.post(`/audits/${id}/submit`, { responses });
      toast.success(`Audit submitted — Score ${data.scores.overall_score}% · ${data.actions_created} corrective action(s) raised`);
      navigate(`/audits/${id}/report`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  const isFailed = (q, r) => {
    if (!r || r.na) return false;
    if (q.type === "yes_no") return r.value === "no";
    if (q.type === "pass_fail") return r.value === "fail";
    if (q.type === "rating_5") return r.value != null && r.value < 3;
    if (q.type === "rating_10") return r.value != null && r.value < 6;
    if (q.type === "percentage") return r.value != null && r.value < 60;
    return false;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-28" data-testid="audit-execution-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/audits")}><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold tracking-tight text-stone-900">{audit.template_name}</h1>
          <p className="text-sm text-stone-500">{audit.hotel_name} · {audit.audit_type} audit</p>
        </div>
      </div>

      <div className="sticky top-16 z-20 bg-white border border-[#E7E5DF] rounded-xl px-5 py-3 flex items-center gap-4 shadow-sm">
        <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#14532D] transition-all" style={{ width: `${progress}%` }} />
        </div>
        {autoSave && (
          <span data-testid="autosave-indicator" className={`text-[11px] font-medium ${autoSave === "saving" ? "text-amber-600" : "text-emerald-600"}`}>
            {autoSave === "saving" ? "Saving…" : "Saved"}
          </span>
        )}
        <span className="text-sm font-mono font-semibold text-stone-700" data-testid="audit-progress">{answered}/{allQuestions.length}</span>
      </div>

      {audit.authorized === false && (
        <div data-testid="authorization-banner" className="flex items-center gap-3 bg-amber-50 border border-amber-300 text-amber-800 rounded-xl px-5 py-4 text-sm">
          <ShieldAlert className="w-5 h-5 shrink-0" />
          This audit is awaiting authorization from the Hotel GM, Regional Head or Admin before responses can be recorded.
        </div>
      )}

      {template.sections.map((s, si) => {
        const assigneeId = (audit.section_assignments || {})[s.id];
        const assigneeName = assigneeId ? (audit.assignment_names || {})[assigneeId] : null;
        const isCollapsed = !!collapsed[s.id];
        const sAnswered = s.questions.filter((q) => { const r = responses[q.id]; return r && (r.na || (r.value !== null && r.value !== undefined && r.value !== "")); }).length;
        return (
        <div key={s.id} className="bg-white border border-[#E7E5DF] rounded-xl overflow-hidden">
          <button
            data-testid={`section-header-${si}`}
            onClick={() => setCollapsed({ ...collapsed, [s.id]: !isCollapsed })}
            className="w-full px-4 lg:px-6 py-4 bg-stone-50 border-b border-[#E7E5DF] flex items-center justify-between gap-2 hover:bg-stone-100/70 transition-colors"
          >
            <span className="font-bold text-stone-800 text-left text-sm lg:text-base">{si + 1}. {s.title}</span>
            <span className="flex items-center gap-2 lg:gap-3 shrink-0">
              {assigneeName && (
                <span className="flex items-center gap-1 text-[11px] font-medium text-[#14532D] bg-[#D1FAE5] px-2 py-0.5 rounded-full">
                  <UserCheck className="w-3 h-3" />{assigneeName}
                </span>
              )}
              <span className="text-[11px] font-mono text-stone-500">{sAnswered}/{s.questions.length} · {s.department}</span>
              <ChevronDown className={`w-4 h-4 text-stone-500 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
            </span>
          </button>
          {!isCollapsed && (
          <div className="divide-y divide-stone-100">
            {s.questions.map((q, qi) => {
              const r = getResp(q.id);
              const failed = isFailed(q, r);
              return (
                <div key={q.id} className={`px-4 lg:px-6 py-5 space-y-3 ${failed ? "bg-rose-50/40" : ""}`} data-testid={`question-block-${si}-${qi}`}>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-sm text-stone-800 leading-relaxed">
                      <span className="font-mono text-stone-400 mr-2">{si + 1}.{qi + 1}</span>
                      {q.text}
                      {q.critical && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-rose-600 uppercase"><AlertTriangle className="w-3 h-3" />Critical</span>}
                    </p>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] font-mono text-stone-400">{q.weight} pts</span>
                      <button
                        onClick={() => setResp(q.id, { ...r, na: !r.na, value: null })}
                        data-testid={`question-na-${si}-${qi}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${r.na ? "bg-stone-700 text-white border-stone-700" : "bg-white text-stone-500 border-stone-300 hover:border-stone-500"}`}
                      >
                        N/A
                      </button>
                    </div>
                  </div>
                  {q.guideline && (
                    <p className="flex items-start gap-1.5 text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
                      <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#14532D]" />{q.guideline}
                    </p>
                  )}
                  {!r.na && <QuestionInput q={q} resp={r} setResp={(nr) => setResp(q.id, nr)} />}
                  {(failed || r.comment) && !r.na && (
                    <Textarea
                      rows={2}
                      className="bg-white text-sm"
                      placeholder={failed ? "Describe the non-compliance finding (becomes a corrective action)..." : "Comment"}
                      value={r.comment || ""}
                      onChange={(e) => setResp(q.id, { ...r, comment: e.target.value })}
                      data-testid={`question-comment-${si}-${qi}`}
                    />
                  )}
                  <div className="flex items-center gap-3 flex-wrap">
                    <button onClick={() => fileRefs.current[`cam-${q.id}`]?.click()} className="flex items-center gap-1.5 text-xs font-medium text-stone-600 hover:text-[#14532D] bg-stone-100 hover:bg-[#D1FAE5] px-2.5 py-1.5 rounded-lg transition-colors" data-testid={`question-photo-${si}-${qi}`}>
                      <Camera className="w-4 h-4" strokeWidth={1.5} />Take photo{q.requires_photo && <span className="text-rose-500">*</span>}
                    </button>
                    <button onClick={() => fileRefs.current[q.id]?.click()} className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#14532D] transition-colors" data-testid={`question-gallery-${si}-${qi}`}>
                      <ImagePlus className="w-4 h-4" strokeWidth={1.5} />Gallery
                    </button>
                    <input ref={(el) => (fileRefs.current[`cam-${q.id}`] = el)} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addPhoto(q.id, e.target.files[0]); e.target.value = ""; }} />
                    <input ref={(el) => (fileRefs.current[q.id] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => { addPhoto(q.id, e.target.files[0]); e.target.value = ""; }} />
                    {attaching[q.id] && (
                      <span data-testid={`photo-attaching-${si}-${qi}`} className="flex items-center gap-1.5 text-xs text-stone-500">
                        <span className="w-3.5 h-3.5 border-2 border-[#14532D] border-t-transparent rounded-full animate-spin" />Attaching…
                      </span>
                    )}
                    {(r.photos || []).map((p, pi) => (
                      <span key={pi} className="relative group">
                        <img src={p.data} alt="evidence" className="w-14 h-14 object-cover rounded-lg border border-stone-200" />
                        <button data-testid={`photo-remove-${si}-${qi}-${pi}`} onClick={() => setResp(q.id, { ...r, photos: r.photos.filter((_, i) => i !== pi) })}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 p-0.5 bg-stone-800 text-white rounded-full opacity-80 hover:opacity-100 flex items-center justify-center">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {!failed && !r.na && !r.comment && (
                      <button className="text-xs text-stone-400 hover:text-stone-600" onClick={() => setResp(q.id, { ...r, comment: " " })}>+ comment</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
        );
      })}

      <div className="fixed bottom-0 left-0 lg:left-64 right-0 bg-white/90 backdrop-blur-xl border-t border-[#E7E5DF] px-4 lg:px-8 py-3 lg:py-4 flex justify-end gap-3 z-30">
        <Button variant="outline" onClick={saveProgress} disabled={saving || audit.authorized === false} data-testid="save-progress-button">
          <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Progress"}
        </Button>
        <Button onClick={submit} disabled={submitting || audit.authorized === false} className="bg-[#14532D] hover:bg-[#166534]" data-testid="submit-audit-button">
          <Send className="w-4 h-4 mr-2" />{submitting ? "Submitting..." : "Submit Audit"}
        </Button>
      </div>
    </div>
  );
}
